import React, { useState } from 'react';
import { TDR } from 'tdr-common';
import { NoTimeSlotsAvailable } from '../callouts/NoTimeslotsAvailable';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { getDisplayTime } from '@/helpers/getDisplayTime';
import { useOutletContext } from 'react-router-dom';
import { AvailabilityGuardContext } from '../../routes/AvailabilityGuard';
import PriceDisplay from './DisplayPrice';

export type TimeslotListProps = {
	restaurant: TDR.Restaurant;
	table: TDR.Table;
	timeslots: [string, TDR.TimeSlot][];
	defaultValue?: string;
	date?: string;
	onChange: (time: string | null, price?: number) => void;
	service?: string;
	includeBookingFeesInfoModal?: boolean;
	hideFreePricing?: boolean;
};

const TimeslotList = ({
	restaurant,
	table,
	timeslots,
	date,
	onChange,
	service,
	hideFreePricing = false
}: TimeslotListProps) => {
	const { searchFilters, featureFlags } = useOutletContext<AvailabilityGuardContext>();

	const [selectedTime, setSelectedTime] = useState<string>(searchFilters?.time || '');

	const toggleGroupBase = 'border-tablz-med-gray-4 bg-tablz-med-gray-2 shadow-sm hover:bg-tablz-med-gray-6';
	const toggleGroupSelected = 'border-tablz-dark-gray-1 bg-tablz-med-gray-6 shadow-selected';

	const getFormattedTime = (time: string) => {
		const displayTime = time ? getDisplayTime(time) : null;
		const [timePart, periodPart] = displayTime ? displayTime.split(' ') : ['', ''];
		return [timePart, periodPart];
	};

	const handleTimeClick = (time, slot) => {
		const price = slot?.regularPrice ?? 0;
		const priceTotaled = slot?.isPerGuest && searchFilters?.groupSize
			? price * searchFilters.groupSize
			: price;
		if (time === selectedTime) {
			onChange('');
		}
		else {
			onChange(time, priceTotaled);
		}
	};

	if (!timeslots) {
		return null;
	}

	if (timeslots.length === 0) {
		return (
			<div style={{ paddingTop: '16px' }}>
				<NoTimeSlotsAvailable
					restaurant={restaurant}
					date={date}
					service={service}
					tableName={table?.name}
				/>
			</div>
		);
	}

	return (
		<div className="">
			<ToggleGroup type="single" className="grid grid-cols-2 gap-3 mt-3" value={selectedTime} onValueChange={(value) => setSelectedTime(value)}>
				{timeslots.map(([time, slot], index) => {
					const showTimeslotPrice = !!table && (slot.regularPrice > 0 || !hideFreePricing);
					return <ToggleGroupItem key={index} value={time} className={`gap-0 p-4 rounded-2xl flex flex-col h-fit border-2 ${time === selectedTime ? toggleGroupSelected : toggleGroupBase}`} onClick={() => handleTimeClick(time, slot)}>
						<div className="flex gap-2">
							<span className="text-tablz-text-white-1 font-poppins text-base font-semibold">{getFormattedTime(time)[0]}</span>
							<span className="text-tablz-text-gray-1 font-poppins text-base">{getFormattedTime(time)[1]}</span>
						</div>
						{showTimeslotPrice ? (
							<PriceDisplay
								restaurant={restaurant}
								table={table}
								time={time}
								searchFilters={searchFilters}
								featureFlags={featureFlags}
							/>
						) : null}
					</ToggleGroupItem>;
				})}
			</ToggleGroup>
		</div>
	);
};

export default TimeslotList;